import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import {
  CareInsightsZendeskArticles,
  ProviderInsightsCompetencyCategory,
  ProviderInsightsSubAreaName,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'

import { BulletedList } from '../../atoms/bulletedList/BulletedList'
import { BulletIcon } from '../../atoms/icons/BulletIcon'
import { RecommendedArticleCard } from '../../atoms/recommendedArticleCard/RecommendedArticleCard'
import { Title } from '../../atoms/title/Title'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { tID } from '../../utils/utils'

export interface CareInsightsRelevantArticlesSubsectionProps {
  competencyAreaName: ProviderInsightsCompetencyCategory
  opportunitySubAreaNames: ProviderInsightsSubAreaName[]
}

const Container = styled.View(({ theme }) => ({
  gap: theme.spacing['12px'],
}))

const RecommendedCardsContainer = styled.View(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  gap: theme.spacing['16px'],
}))

const BulletIconContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['12px'],
}))

const TwoColumnContainer = styled.View(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthTablet ? 'row' : 'column',
  gap: theme.spacing['16px'],
}))

const ColumnContainer = styled.View({
  flex: 1,
})

type ArticleType = {
  id: string
  title: string
  url: string
  relatedSubAreas: ProviderInsightsSubAreaName[]
}

export const CareInsightsRelevantArticlesSubsection: React.FC<CareInsightsRelevantArticlesSubsectionProps> = ({
  competencyAreaName,
  opportunitySubAreaNames,
}) => {
  const { formatMessage } = useIntl()
  const { colors, spacing, breakpoints } = useTheme()

  const { recommendedArticles, otherArticles } = useMemo(() => {
    const allArticles: ArticleType[] =
      (CareInsightsZendeskArticles as Record<ProviderInsightsCompetencyCategory, ArticleType[] | undefined>)[
        competencyAreaName
      ] || []

    const recommended: ArticleType[] = []
    const checkedUrls = new Set<string>()

    for (const article of allArticles) {
      const hasMatchingSubArea = article.relatedSubAreas.some((subArea: ProviderInsightsSubAreaName) =>
        opportunitySubAreaNames.includes(subArea),
      )
      if (hasMatchingSubArea && !checkedUrls.has(article.url)) {
        recommended.push(article)
        checkedUrls.add(article.url)
      }
    }

    const other = allArticles.filter((article: ArticleType) => !checkedUrls.has(article.url))

    return { recommendedArticles: recommended, otherArticles: other }
  }, [competencyAreaName, opportunitySubAreaNames])

  return (
    <Container testID={tID('CareInsightsRelevantArticlesSubsection')}>
      <Title
        text={formatMessage({
          defaultMessage: 'Relevant articles',
          description: 'Header for relevant articles subsection in Care Insights',
        })}
        size={TitleSize.MEDIUM}
      />
      {recommendedArticles.length > 0 && (
        <RecommendedCardsContainer>
          {recommendedArticles.map((article) => (
            <RecommendedArticleCard
              key={article.url}
              id={article.id}
              title={article.title}
              onPress={() => openUrl(article.url)}
            />
          ))}
        </RecommendedCardsContainer>
      )}
      {otherArticles.length > 0 &&
        (otherArticles.length > 4 && breakpoints.isMinWidthTablet ? (
          <TwoColumnContainer>
            {[0, 1].map((columnIndex) => {
              const midpoint = Math.ceil(otherArticles.length / 2)
              const columnArticles =
                columnIndex === 0 ? otherArticles.slice(0, midpoint) : otherArticles.slice(midpoint)
              return (
                <ColumnContainer key={columnIndex}>
                  <BulletedList
                    data={columnArticles.map((article) => ({
                      value: article.title,
                      onPress: () => openUrl(article.url),
                      testID: `ArticleLink-${article.id}`,
                      bulletIcon: (
                        <BulletIconContainer>
                          <BulletIcon size={2} fillColor={colors.textActive} />
                        </BulletIconContainer>
                      ),
                    }))}
                    size={BodyTextSize.DEFAULT}
                    spaceBetween={0}
                    style={{ marginLeft: spacing['12px'] }}
                  />
                </ColumnContainer>
              )
            })}
          </TwoColumnContainer>
        ) : (
          <BulletedList
            data={otherArticles.map((article) => ({
              value: article.title,
              onPress: () => openUrl(article.url),
              testID: `ArticleLink-${article.id}`,
              bulletIcon: (
                <BulletIconContainer>
                  <BulletIcon size={2} fillColor={colors.textActive} />
                </BulletIconContainer>
              ),
            }))}
            size={BodyTextSize.DEFAULT}
            spaceBetween={0}
            style={{ marginLeft: spacing['12px'] }}
          />
        ))}
    </Container>
  )
}

export default CareInsightsRelevantArticlesSubsection
